import { useEffect, useState, type ReactNode } from 'react';
import { SettingsGlyph } from './icons';
import {
  clampRailMotion,
  DEFAULT_RAIL_MOTION,
  RAIL_EFFECT_RANGES,
  type RailMotion,
  type RailEffect,
} from './railMotion';
import {
  clampMotion,
  DEFAULT_QUEUE_MOTION,
  type QueueMotion,
} from './queueMotion';
export interface InternalParams {
  simulatedCoins: boolean;
  simulatedTrading: boolean;
  queue: QueueMotion;
  rails: RailMotion;
}
const KEY = 'pons-internal-params-v1';
const OFF: InternalParams = {
  simulatedCoins: false,
  simulatedTrading: false,
  queue: DEFAULT_QUEUE_MOTION,
  rails: DEFAULT_RAIL_MOTION,
};
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];
export function internalToolsVisible() {
  try {
    return (
      LOCAL_HOSTS.includes(window.location.hostname) ||
      new URLSearchParams(window.location.search).has('internal')
    );
  } catch {
    return false;
  }
}
function load(): InternalParams {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (raw && typeof raw === 'object')
      return {
        simulatedCoins:
          (raw as Record<string, unknown>).simulatedCoins === true,
        simulatedTrading:
          (raw as Record<string, unknown>).simulatedTrading === true,
        queue: clampMotion(
          (raw as Record<string, unknown>).queue as Partial<QueueMotion>,
        ),
        rails: clampRailMotion((raw as Record<string, unknown>).rails),
      };
  } catch {}
  return OFF;
}
export function useInternalParams() {
  const [visible] = useState(internalToolsVisible);
  const [params, setParams] = useState<InternalParams>(() =>
    visible ? load() : OFF,
  );
  useEffect(() => {
    if (!visible) return;
    try {
      localStorage.setItem(KEY, JSON.stringify(params));
    } catch {}
  }, [params, visible]);
  return { visible, params, setParams };
}
function Slider({
  label,
  value,
  min,
  max,
  step,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix: string;
  onChange: (value: number) => void;
}) {
  const shown = Number.isInteger(value) ? String(value) : value.toFixed(2);
  return (
    <label className="internal-slider" aria-label={label}>
      <span>
        <code>{label}</code>
        <b>
          {shown}
          {suffix}
        </b>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}
function Switch({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="internal-row" aria-label={label}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="internal-track" aria-hidden="true">
        <i />
      </span>
      <span className="internal-text">
        <code>{label}</code>
        <span>{hint}</span>
      </span>
    </label>
  );
}
function Fold({
  id,
  label,
  meta,
  open,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  meta?: string;
  open: boolean;
  onToggle: (id: string) => void;
  children: ReactNode;
}) {
  return (
    <section className={'internal-fold' + (open ? ' is-open' : '')}>
      <button
        type="button"
        className="internal-fold-head"
        aria-expanded={open}
        onClick={() => onToggle(id)}
      >
        <span>
          {open ? '▾' : '▸'} {label}
        </span>
        {meta ? <code>{meta}</code> : null}
      </button>
      {open && <div className="internal-fold-body">{children}</div>}
    </section>
  );
}
export function InternalPanel({
  params,
  onChange,
  simulatedCount,
  onCreateToken,
  onClearAll,
}: {
  params: InternalParams;
  onChange: (params: InternalParams) => void;
  simulatedCount: number;
  onCreateToken: (name: string, symbol: string) => void;
  onClearAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [folds, setFolds] = useState({
    sim: true,
    card: true,
    queue: true,
    rails: true,
    spawn: true,
  });
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const active = params.simulatedCoins || params.simulatedTrading;
  const queue = params.queue;
  const rails = params.rails ?? DEFAULT_RAIL_MOTION;
  const patch = (next: Partial<QueueMotion>) =>
    onChange({ ...params, queue: { ...queue, ...next } });
  const toggle = (id: keyof typeof folds) =>
    setFolds((current) => ({ ...current, [id]: !current[id] }));
  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [open]);
  return (
    <div className="internal-tools">
      {open && (
        <section
          className="internal-panel"
          role="dialog"
          aria-label="Internal params"
        >
          <header>
            <b>params</b>
            <span>local debug · never posted</span>
          </header>
          <Fold
            id="sim"
            label="sim"
            meta={params.simulatedCoins ? 'n=' + simulatedCount : 'chain-only'}
            open={folds.sim}
            onToggle={() => toggle('sim')}
          >
            <Switch
              label="sim.coins"
              hint="Invented tokens. Not sent to the server."
              checked={params.simulatedCoins}
              onChange={(simulatedCoins) =>
                onChange({ ...params, simulatedCoins })
              }
            />
            <Switch
              label="sim.trading"
              hint="Invented opponents on those tokens."
              checked={params.simulatedTrading}
              onChange={(simulatedTrading) =>
                onChange({ ...params, simulatedTrading })
              }
            />
          </Fold>
          <Fold
            id="card"
            label="card"
            meta={queue.cardMs + 'ms'}
            open={folds.card}
            onToggle={() => toggle('card')}
          >
            <Slider
              label="card.ms"
              suffix="ms"
              min={80}
              max={2000}
              step={10}
              value={queue.cardMs}
              onChange={(cardMs) => patch({ cardMs })}
            />
            <Slider
              label="card.scale"
              suffix=""
              min={0.6}
              max={1.2}
              step={0.01}
              value={queue.cardScale}
              onChange={(cardScale) => patch({ cardScale })}
            />
            <Slider
              label="card.blur"
              suffix="px"
              min={0}
              max={24}
              step={0.5}
              value={queue.cardBlur}
              onChange={(cardBlur) => patch({ cardBlur })}
            />
            <Slider
              label="card.rise"
              suffix="px"
              min={0}
              max={80}
              step={1}
              value={queue.cardRise}
              onChange={(cardRise) => patch({ cardRise })}
            />
            <Slider
              label="card.shake"
              suffix="px"
              min={0}
              max={24}
              step={1}
              value={queue.cardShake}
              onChange={(cardShake) => patch({ cardShake })}
            />
            <Slider
              label="card.cascade"
              suffix="ms"
              min={0}
              max={200}
              step={4}
              value={queue.cardCascade}
              onChange={(cardCascade) => patch({ cardCascade })}
            />
          </Fold>
          <Fold
            id="queue"
            label="queue"
            meta={queue.tapeMs + 'ms'}
            open={folds.queue}
            onToggle={() => toggle('queue')}
          >
            <p className="internal-sub">tape</p>
            <Slider
              label="tape.ms"
              suffix="ms"
              min={80}
              max={2000}
              step={10}
              value={queue.tapeMs}
              onChange={(tapeMs) => patch({ tapeMs })}
            />
            <Slider
              label="tape.scale"
              suffix=""
              min={0.4}
              max={1}
              step={0.01}
              value={queue.tapeScale}
              onChange={(tapeScale) => patch({ tapeScale })}
            />
            <Slider
              label="tape.blur"
              suffix="px"
              min={0}
              max={20}
              step={0.5}
              value={queue.tapeBlur}
              onChange={(tapeBlur) => patch({ tapeBlur })}
            />
            <Slider
              label="tape.bounce"
              suffix=""
              min={0}
              max={1}
              step={0.01}
              value={queue.tapeBounce}
              onChange={(tapeBounce) => patch({ tapeBounce })}
            />
            <p className="internal-sub">enter</p>
            <Slider
              label="enter.ms"
              suffix="ms"
              min={80}
              max={2000}
              step={10}
              value={queue.enterMs}
              onChange={(enterMs) => patch({ enterMs })}
            />
            <Slider
              label="enter.scale"
              suffix=""
              min={0.2}
              max={1.4}
              step={0.01}
              value={queue.enterScale}
              onChange={(enterScale) => patch({ enterScale })}
            />
            <Slider
              label="enter.blur"
              suffix="px"
              min={0}
              max={24}
              step={0.5}
              value={queue.enterBlur}
              onChange={(enterBlur) => patch({ enterBlur })}
            />
            <p className="internal-sub">exit</p>
            <Slider
              label="exit.ms"
              suffix="ms"
              min={80}
              max={2000}
              step={10}
              value={queue.exitMs}
              onChange={(exitMs) => patch({ exitMs })}
            />
            <Slider
              label="exit.scale"
              suffix=""
              min={0.2}
              max={1.4}
              step={0.01}
              value={queue.exitScale}
              onChange={(exitScale) => patch({ exitScale })}
            />
            <Slider
              label="exit.blur"
              suffix="px"
              min={0}
              max={24}
              step={0.5}
              value={queue.exitBlur}
              onChange={(exitBlur) => patch({ exitBlur })}
            />
            <p className="internal-sub">hover</p>
            <Slider
              label="hover.scale"
              suffix=""
              min={1}
              max={1.6}
              step={0.01}
              value={queue.hoverScale}
              onChange={(hoverScale) => patch({ hoverScale })}
            />
            <Slider
              label="hover.ms"
              suffix="ms"
              min={80}
              max={2000}
              step={10}
              value={queue.hoverMs}
              onChange={(hoverMs) => patch({ hoverMs })}
            />
            <Slider
              label="hover.spin"
              suffix="deg"
              min={0}
              max={360}
              step={15}
              value={queue.hoverSpin}
              onChange={(hoverSpin) => patch({ hoverSpin })}
            />
            <div
              className="internal-axis"
              role="radiogroup"
              aria-label="hover.axis"
            >
              <code>hover.axis</code>
              <span>
                {(['x', 'y', 'z'] as const).map((axis) => (
                  <button
                    key={axis}
                    type="button"
                    aria-pressed={queue.hoverAxis === axis}
                    onClick={() => patch({ hoverAxis: axis })}
                  >
                    {axis}
                  </button>
                ))}
              </span>
            </div>
          </Fold>
          <Fold
            id="rails"
            label="rails"
            meta="left + right · max 10"
            open={folds.rails}
            onToggle={() => toggle('rails')}
          >
            <p className="internal-sub">
              New coins use queue → enter settings.
            </p>
            {(['hover', 'others', 'preview'] as const).map((effect) => (
              <div key={effect}>
                <p className="internal-sub">{effect}</p>
                {(['scale', 'blur', 'ms'] as const).map((field) => (
                  <Slider
                    key={field}
                    label={'rails.' + effect + '.' + field}
                    {...RAIL_EFFECT_RANGES[field]}
                    value={rails[effect][field]}
                    onChange={(value) => {
                      const next: RailEffect = {
                        ...rails[effect],
                        [field]: value,
                      };
                      onChange({
                        ...params,
                        rails: { ...rails, [effect]: next },
                      });
                    }}
                  />
                ))}
              </div>
            ))}
          </Fold>
          {params.simulatedCoins && (
            <Fold
              id="spawn"
              label="sim.spawn"
              open={folds.spawn}
              onToggle={() => toggle('spawn')}
            >
              <label className="internal-field">
                <span>name</span>
                <input
                  type="text"
                  value={name}
                  maxLength={24}
                  placeholder="Orbit Cat"
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label className="internal-field">
                <span>ticker</span>
                <input
                  type="text"
                  value={symbol}
                  maxLength={8}
                  placeholder="OCAT"
                  onChange={(event) => setSymbol(event.target.value)}
                />
              </label>
              <div className="internal-actions">
                <button
                  type="button"
                  className="internal-action"
                  onClick={() => {
                    onCreateToken(name, symbol);
                    setName('');
                    setSymbol('');
                  }}
                >
                  create
                </button>
                <button
                  type="button"
                  className="internal-action is-danger"
                  disabled={!simulatedCount}
                  onClick={onClearAll}
                >
                  clear
                </button>
              </div>
            </Fold>
          )}
        </section>
      )}
      <button
        type="button"
        className={'internal-fab' + (active ? ' is-active' : '')}
        aria-expanded={open}
        aria-label="Internal params"
        title="params"
        onClick={() => setOpen((current) => !current)}
      >
        <SettingsGlyph />
        {active && <i aria-hidden="true" />}
      </button>
    </div>
  );
}
